import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ShieldCheck, ExternalLink, AlertTriangle, Loader2, LifeBuoy, Clock } from "lucide-react";
import { toast } from "sonner";
import PreCallCheck from "@/components/PreCallCheck";
import { BRAND } from "@/config/brand";

const JAAS_DOMAIN = "8x8.vc";

// "2026-08-22" + "13:10" -> "Sat, 22 Aug at 1:10 pm" (falls back to the raw
// strings if parsing fails, e.g. bad data — never crash the page over it).
function formatSessionTime(date, time) {
  try {
    const dt = new Date(`${date}T${time}:00`);
    if (Number.isNaN(dt.getTime())) return `${date} at ${time}`;
    const datePart = dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    const timePart = dt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();
    return `${datePart} at ${timePart}`;
  } catch {
    return `${date} at ${time}`;
  }
}

// Load the JaaS External API script exactly once, scoped by AppID.
function loadJaasApi(appId) {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.JitsiMeetExternalAPI) return Promise.resolve(window.JitsiMeetExternalAPI);
  const src = `https://${JAAS_DOMAIN}/${appId}/external_api.js`;
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.JitsiMeetExternalAPI));
      existing.addEventListener("error", () => reject(new Error("JaaS script failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve(window.JitsiMeetExternalAPI);
    s.onerror = () => reject(new Error("JaaS script failed to load"));
    document.head.appendChild(s);
  });
}

export default function VideoSession() {
  const { id } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState("");
  const [errCode, setErrCode] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | precall | not-yet | window-closed | joining | in-call | ended | error
  const [scriptError, setScriptError] = useState(false);
  const [notYetMessage, setNotYetMessage] = useState("");
  const [jaas, setJaas] = useState(null); // { jwt, app_id, room, domain }
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  // Fetch booking; backend enforces client_id/counselor_id/admin access.
  const loadBooking = () => {
    let alive = true;
    setPhase("loading");
    api.get(`/bookings/${id}`)
      .then(r => {
        if (!alive) return;
        setBooking(r.data);
        if (r.data.status === "confirmed" || r.data.status === "completed") {
          // Check the join window up-front (before ever asking for camera/mic)
          // so an early or late visit gets a calm, accurate message instead
          // of failing later with a generic "couldn't start" warning.
          if (r.data.window_state === "future") {
            setNotYetMessage(`This session hasn't started yet. Please come back on ${formatSessionTime(r.data.date, r.data.start_time)}.`);
            setPhase("not-yet");
          } else if (r.data.window_state === "past_incomplete") {
            setNotYetMessage("The session window has passed. Please reschedule to continue.");
            setPhase("window-closed");
          } else {
            setPhase("precall");
          }
        } else {
          setPhase("error");
          setErr("This session isn't confirmed yet. Please complete payment to unlock the room.");
        }
      })
      .catch(e => {
        if (!alive) return;
        const code = e?.response?.status;
        setErrCode(code);
        setPhase("error");
        if (code === 403) {
          setErr("You don't have access to this session. Only the client and their assigned counselor can join.");
        } else if (code === 404) {
          setErr("Session not found. The link may be broken or the booking was cancelled.");
        } else {
          setErr(e?.response?.data?.detail || "Could not load the session.");
        }
      });
    return () => { alive = false; };
  };
  useEffect(loadBooking, [id]);

  // Best-effort session event ping.
  const postEvent = async (event_type) => {
    try {
      await api.post(`/bookings/${id}/session-event`, { event_type });
    } catch (e) {
      console.warn("session-event failed:", e?.message || e);
    }
  };

  // Boot JaaS once user completes the pre-call check.
  const handleReady = async () => {
    setPhase("joining");
    setScriptError(false);
    try {
      // 1. Ask the backend for a short-lived RS256 JWT signed with our JaaS private key.
      const tokenResp = await api.post(`/bookings/${id}/jaas-token`);
      const tok = tokenResp.data;
      if (!tok?.jwt || !tok?.app_id || !tok?.room) {
        throw new Error("Video service token could not be issued.");
      }
      setJaas(tok);
      // 2. Load the tenant-scoped External API and mount the iframe.
      const JitsiMeetExternalAPI = await loadJaasApi(tok.app_id);
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";
      const displayName = user?.name || (user?.role === "counselor" ? "Counselor" : "Client");
      const email = user?.email || "";
      const options = {
        roomName: tok.room,           // "<AppID>/<room>" — required by JaaS
        jwt: tok.jwt,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName, email },
        configOverwrite: {
          prejoinPageEnabled: true,
          disableThirdPartyRequests: true,
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          enableWelcomePage: false,
          enableClosePage: false,
          disableDeepLinking: true,
          p2p: { enabled: true },
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          DEFAULT_BACKGROUND: "#0a0a0a",
        },
      };
      const api2 = new JitsiMeetExternalAPI(JAAS_DOMAIN, options);
      apiRef.current = api2;

      api2.addEventListener("videoConferenceJoined", () => {
        setPhase("in-call");
        postEvent("joined");
        if (user?.role === "counselor") {
          api.post(`/bookings/${id}/counselor-ready`, {}).catch(e =>
            console.warn("counselor-ready ping failed:", e?.message || e)
          );
        }
      });
      api2.addEventListener("videoConferenceLeft", () => {
        postEvent("left");
        setPhase("ended");
      });
      api2.addEventListener("readyToClose", () => {
        postEvent("ended");
        setPhase("ended");
      });
    } catch (e) {
      console.error("JaaS boot failed:", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400) {
        // A guard rejection (window not open, needs feedback first, etc.) —
        // show the same calm notice as the up-front check, not a red
        // "technical failure" warning with a misleading "try again" retry.
        setNotYetMessage(detail || "This session can't be joined right now.");
        setPhase("not-yet");
        return;
      }
      if (detail) toast.error(detail);
      setScriptError(true);
      setPhase("precall");
    }
  };

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (apiRef.current) {
        try { apiRef.current.dispose(); } catch (_) {}
        apiRef.current = null;
      }
    };
  }, []);

  // Best-effort fallback URL (JaaS also allows viewing directly with the JWT).
  const rawFallbackUrl = jaas
    ? `https://${JAAS_DOMAIN}/${jaas.room}?jwt=${jaas.jwt}`
    : "";

  if (phase === "loading") {
    return (
      <div className="p-16 text-center" data-testid="video-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
        Loading session…
      </div>
    );
  }

  if (phase === "not-yet" || phase === "window-closed") {
    const isFuture = phase === "not-yet";
    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="video-join-notice">
        <Link to="/dashboard" className="btn-outline inline-flex items-center gap-2 mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="card-soft p-8 text-center">
          <Clock className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--brand)" }} />
          <h1 className="font-serif-display text-2xl mb-2">
            {isFuture ? "Not quite time yet" : "Session window has closed"}
          </h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{notYetMessage}</p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <Link to="/dashboard" className="btn-primary text-sm">Back to dashboard</Link>
            {isFuture && (
              <button data-testid="video-join-notice-recheck"
                onClick={loadBooking}
                className="btn-outline text-sm">
                Check again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="video-error">
        <Link to="/dashboard" className="btn-outline inline-flex items-center gap-2 mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="card-soft p-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--brand)" }} />
          <h1 className="font-serif-display text-2xl mb-2">
            {errCode === 403 ? "Access denied" : errCode === 404 ? "Session not found" : "Session unavailable"}
          </h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{err}</p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <Link to="/dashboard" className="btn-primary text-sm">Go to dashboard</Link>
            <a href={`mailto:${BRAND.bookingSupportEmail}?subject=Session%20${id}%20problem`}
               className="btn-outline text-sm inline-flex items-center gap-1"
               data-testid="video-report-problem">
              <LifeBuoy className="w-4 h-4" /> Report a problem
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <Link to="/dashboard" className="btn-outline inline-flex items-center gap-2 mb-6 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="card-soft p-6 mb-4">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow">Session</span>
            <h1 className="font-serif-display text-3xl mt-1">{booking.service_name}</h1>
            <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              {booking.date} · {booking.start_time}–{booking.end_time} · with {user?.role === "counselor" ? booking.client_name : booking.counselor_name}
            </div>
          </div>
          {rawFallbackUrl && (
            <a data-testid="open-in-new" href={rawFallbackUrl} target="_blank" rel="noreferrer" className="btn-outline text-sm inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4" /> Open in new tab
            </a>
          )}
        </div>

        <div className="mt-4 flex items-start gap-3 rounded-lg p-3"
             style={{ background: "rgba(91,117,83,0.08)" }} data-testid="privacy-banner">
          <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--brand)" }} />
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            <div className="font-semibold text-sm mb-0.5" style={{ color: "var(--text-primary)" }}>
              Private 1-to-1 · This session is not recorded
            </div>
            End-to-end encrypted video powered by 8x8 JaaS. Only you and your counselor can join. Please stay in a quiet, private space.
          </div>
        </div>
      </div>

      {phase === "precall" && booking && (
        <>
          {scriptError && (
            <div className="card-soft p-4 mb-4 text-sm flex items-start gap-3"
                 style={{ borderLeft: "4px solid var(--brand)" }}
                 data-testid="video-fallback-notice">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: "var(--brand)" }} />
              <div>
                <div className="font-semibold">The in-page video couldn't start.</div>
                <div className="mt-1" style={{ color: "var(--text-secondary)" }}>
                  This can happen on restrictive networks (school/office Wi-Fi) or if the video service token could not be issued. Try again, use a different network, or contact support.
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={handleReady} className="btn-primary text-sm">Try again</button>
                  <a href={`mailto:${BRAND.bookingSupportEmail}?subject=Session%20${id}%20cannot%20start`}
                     className="btn-outline text-sm">Report a problem</a>
                </div>
              </div>
            </div>
          )}
          <PreCallCheck onReady={handleReady} participantLabel={user?.name} />
        </>
      )}

      {(phase === "joining" || phase === "in-call") && (
        <div className="card-soft overflow-hidden relative" style={{ height: "72vh" }} data-testid="jitsi-frame">
          {phase === "joining" && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/40 text-white text-sm pointer-events-none">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connecting to the room…
            </div>
          )}
          <div ref={containerRef} className="w-full h-full" />
        </div>
      )}

      {phase === "ended" && (
        <div className="card-soft p-8 text-center" data-testid="session-ended">
          <ShieldCheck className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--brand)" }} />
          <h2 className="font-serif-display text-2xl mb-1">Session ended</h2>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Thanks for using {BRAND.name}. You can safely close this tab.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <button onClick={handleReady} className="btn-outline text-sm" data-testid="session-rejoin">Rejoin</button>
            <Link to="/dashboard" className="btn-primary text-sm">Back to dashboard</Link>
          </div>
        </div>
      )}
    </div>
  );
}
